import type { ReactNode } from 'react'

import { useCallback, useEffect, useRef } from 'react'

import type { ToastDuration, ToastOptions, ToastStatus } from './toast.types'

import { useVuiContext } from '../core/vuiProvider/context'
import { uid } from '../utils'
import { ToastItem } from './Toaster'

export const defaultDuration = 'fast'
export const fastToastDuration = 4000
export const slowToastDuration = 8000

const DEFAULT_DURATION_BY_STATUS: Record<string, ToastDuration> = {
  info: 'fast',
  success: 'fast',
  warning: 'fast',
  danger: 'slow',
  error: 'slow',
  loading: 'sticky',
}

const getDurationTime = (duration: Exclude<ToastDuration, 'sticky'>): number => {
  switch (duration) {
    case 'fast':
      return fastToastDuration
    case 'slow':
      return slowToastDuration
    default: {
      const _exhaustive: never = duration
      throw new Error(`[VUI] useToast: unhandled duration "${_exhaustive}"`)
    }
  }
}

export const useToast = () => {
  const { addToast, removeToast } = useVuiContext()
  const timeoutMapRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timeouts = timeoutMapRef.current
    return () => {
      timeouts.forEach(clearTimeout)
      timeouts.clear()
    }
  }, [])

  const showToast = useCallback(
    (options?: ToastOptions): void => {
      if (process.env.NODE_ENV !== 'production' && options?.dismissible !== undefined) {
        console.warn(
          '[VUI] Toast: `dismissible` is deprecated. Use `showDismissButton` to control dismiss button visibility.',
        )
      }
      const id = options?.id ? options.id : uid('toast')
      const showDismissButton = options?.showDismissButton ?? options?.dismissible ?? true
      // Normalise deprecated 'error' status to 'danger'.
      const status = (options?.status === 'error' ? 'danger' : (options?.status ?? 'info')) as ToastStatus
      const {
        duration = DEFAULT_DURATION_BY_STATUS[status],
        action,
        icon,
        size,
        text,
        textLayout,
        title,
      } = options || {}

      const handleClose = () => {
        const timeoutId = timeoutMapRef.current.get(id)
        if (timeoutId !== undefined) clearTimeout(timeoutId)
        timeoutMapRef.current.delete(id)
        removeToast(id)
      }

      const toast = {
        id,
        component: (
          <ToastItem
            action={action}
            icon={icon}
            showDismissButton={showDismissButton}
            size={size}
            status={status}
            text={text}
            textLayout={textLayout}
            title={title}
            onClose={handleClose}
          />
        ),
      }

      addToast(toast)

      if (duration && duration !== 'sticky') {
        timeoutMapRef.current.set(
          id,
          setTimeout(() => {
            timeoutMapRef.current.delete(id)
            removeToast(id)
          }, getDurationTime(duration)),
        )
      }
    },
    [addToast, removeToast],
  )

  const showInfo = useCallback(
    (text: ReactNode, options?: Omit<ToastOptions, 'text' | 'status'>) =>
      showToast({ text, status: 'info', ...options }),
    [showToast],
  )

  const showSuccess = useCallback(
    (text: ReactNode, options?: Omit<ToastOptions, 'text' | 'status'>) =>
      showToast({ text, status: 'success', ...options }),
    [showToast],
  )

  const showWarning = useCallback(
    (text: ReactNode, options?: Omit<ToastOptions, 'text' | 'status'>) =>
      showToast({ text, status: 'warning', ...options }),
    [showToast],
  )

  const showDanger = useCallback(
    (text: ReactNode, options?: Omit<ToastOptions, 'text' | 'status'>) =>
      showToast({ text, status: 'danger', ...options }),
    [showToast],
  )

  /** @deprecated Use `showDanger` instead. */
  const showError = showDanger

  const showLoading = useCallback(
    (text: ReactNode, options?: Omit<ToastOptions, 'text' | 'status'>) =>
      showToast({ text, status: 'loading', ...options }),
    [showToast],
  )

  return {
    showToast,
    hideToast: removeToast,
    showInfo,
    showSuccess,
    showWarning,
    showDanger,
    showError,
    showLoading,
  }
}
